import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AlertCircle } from "lucide-react";

export default function Login() {
  const { user, login, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  if (!loading && user) {
    if (user.must_change_password) return <Navigate to="/change-password" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      if (res.user?.must_change_password) nav("/change-password", { replace: true });
      else nav("/dashboard", { replace: true });
    } else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8FAFC]">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-white border-r border-slate-200 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <img src="/solemnity-logo.jpg" alt="Solemnity logo" className="w-12 h-12 rounded-lg object-contain bg-white border border-slate-100" />
          <div>
            <div className="font-heading text-lg font-semibold">Solemnity IT Healthcare Solutions Pvt Ltd</div>
            <div className="text-xs text-slate-500 uppercase tracking-widest">Delivers Quality</div>
          </div>
        </div>

        <div className="max-w-md">
          <h1 className="font-heading text-4xl font-semibold text-slate-900 tracking-tight leading-[1.1]">
            Cleaner charts.<br /> Sharper audits.<br /> Better outcomes.
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed">
            A focused workspace for coders, QAs, managers and admins — from chart submission
            to error tracking, accuracy and turnaround in one place.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-4 text-sm">
            {[
              ["98.4%", "avg accuracy"],
              ["12h", "median TAT"],
              ["5", "error categories"],
            ].map(([v, l]) => (
              <div key={l} className="border border-slate-200 rounded-xl p-4 bg-white">
                <div className="font-heading text-2xl font-semibold text-slate-900">{v}</div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-slate-400">© {new Date().getFullYear()} Solemnity IT Healthcare Solutions Pvt Ltd — HIPAA-minded workflow</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <img src="/solemnity-logo.jpg" alt="Solemnity logo" className="w-10 h-10 rounded-lg object-contain bg-white border border-slate-100" />
            <span className="font-heading font-semibold">Solemnity IT Healthcare Solutions Pvt Ltd</span>
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">Sign in</h2>
          <p className="text-slate-500 text-sm mt-1">Use your work email to access the audit workspace.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@hospital.org"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 outline-none"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="w-full bg-sky-700 text-white rounded-lg py-2.5 font-medium hover:bg-sky-600 transition-colors disabled:opacity-60"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
